import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { postRequest } from '../../../services/base'
import { NavContext } from '../../../context/global_context'
import DtcMappingRows from './DtcMappingRows'
import { Button, Card, Col, Flex, Modal, Row, Space, Typography } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

const DtcMappingItem = (props) => {
  const intl = useIntl()
  const [navState, navDispatch] = useContext(NavContext)
  const { name, ecu, mappings, circuits, id, onDtcMappingChange, description, wrapperClass } =
    props
  const [modal, contextHolder] = Modal.useModal()

  const confirmDeleteMapping = (mapping_id, category, name) => {
    const title = intl.formatMessage({ id: 'dtcs.mappings.confirmMappingDeletion' })
    const content = (category === 'clear_all') ? intl.formatMessage({ id: 'dtcs.mappings.confirmAllMappingDeleteContent' }, { name: name }) : intl.formatMessage({ id: 'dtcs.mappings.confirmMappingDeleteContent' }, { name: name })

    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk: () => {
        deleteMapping(mapping_id, category, name)
      },
      centered: true,
      maskClosable: true,
    })
  }
  const deleteMapping = async(mapping_id, category, name) => {
    let res = await postRequest(`/api/dtcs/clear_mappings/`, {
      category: category,
      id: mapping_id,
      dtc_id: id,
      name: name,
    })
    // `res?.errors < 1` is false when errors is undefined, so a failed request
    // fell past the success branch and reported nothing at all.
    if (res?.status === false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'dtcs.mappings.deleteFailed' }),
        },
      })
      return
    }
    if (res?.errors < 1) {
      onDtcMappingChange()

      const start = intl.formatMessage({ id: 'generic.start' })
      const end = intl.formatMessage({ id: 'generic.end' })
      const isConnector = [start, end].includes(category)
      const message = category === 'clear_all' ? (
        intl.formatMessage({ id: 'dtcs.mappings.allMappingsDeleted' }, { name: name || '' })
      ) : (
        intl.formatMessage({ 
          id: 'dtcs.mappings.mappingDeleted' 
        }, 
        { 
          name: name || '', 
          category: category, 
          type: isConnector ? intl.formatMessage({ id: 'generic.connector' }) : ''
        })
      )

      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: message,
        },
      })
    }
  }

  return (
    <>
      <Card title="" variant="outlined" className={wrapperClass} data-testid="dtc-mapping-card" data-name={name}>
        <Flex vertical gap={0}>
          <Flex className="dtc-grid-row">
            <Typography.Text className="col-title bolder">{intl.formatMessage({ id: 'generic.dtc' })}:</Typography.Text>
            <Flex
              align="flex-start"
              justify="space-between"
              className="col-content full-width"
            >
              <Flex vertical gap={0}>
                <Typography.Text className='dtc-name bolder'>{name}</Typography.Text>
                <Typography.Text className='dtc-desc'>{description}</Typography.Text>
              </Flex>
              <Button type="link" className="link-button action danger" data-testid="dtc-clear-mappings" onClick={() => {
                confirmDeleteMapping('', 'clear_all', name)
              }}>
                {intl.formatMessage({ id: 'dtcs.mappings.clearMappings' })}
              </Button>
            </Flex>
          </Flex>

          <Flex className="dtc-grid-row">
            <Typography.Text className="col-title bolder">{intl.formatMessage({ id: 'generic.ECU' })}:</Typography.Text>
            <Flex
              align="center"
              justify="space-between"
              className="col-content full-width no-action"
            >
              <Typography.Text className='ecu-name bolder'>{ecu}</Typography.Text>
            </Flex>
          </Flex>

          <DtcMappingRows
            mappings={mappings?.start}
            category={intl.formatMessage({ id: 'generic.start' })}
            deleteMapping={(id, category, name) =>
              confirmDeleteMapping(id, category, name)
            }
          />
          <DtcMappingRows
            mappings={mappings?.end}
            category={intl.formatMessage({ id: 'generic.end' })}
            deleteMapping={(id, category, name) =>
              confirmDeleteMapping(id, category, name)
            }
          />
          <DtcMappingRows
            mappings={mappings?.devices}
            category={intl.formatMessage({ id: 'generic.devices' })}
            deleteMapping={(id, category, name) =>
              confirmDeleteMapping(id, category, name)
            }
          />
          <DtcMappingRows
            mappings={circuits}
            category={intl.formatMessage({ id: 'generic.circuits' })}
            deleteMapping={(id, category, name) =>
              confirmDeleteMapping(id, category, name)
            }
          />
        </Flex>
      </Card>

      {contextHolder}
    </>
  )
}

DtcMappingItem.propTypes = {
  name: PropTypes.string.isRequired,
  ecu: PropTypes.string.isRequired,
  mappings: PropTypes.object.isRequired,
  circuits: PropTypes.array.isRequired,
}

export default DtcMappingItem
